/**
 * Smooth Scroll Module
 *
 * Initializes Lenis smooth scrolling with GSAP integration.
 *
 * @package TheRealEstates
 */

import Lenis from 'lenis';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

export default class SmoothScroll {
  constructor(options = {}) {
    this.options = {
      lerp: 0.1,
      duration: 1.2,
      smoothWheel: true,
      wheelMultiplier: 1,
      touchMultiplier: 2,
      infinite: false,
      ...options,
    };

    this.lenis = null;
    this.init();
  }

  /**
   * Initialize Lenis
   */
  init() {
    // Create Lenis instance
    this.lenis = new Lenis(this.options);

    // Sync Lenis with GSAP ScrollTrigger
    this.lenis.on('scroll', ScrollTrigger.update);

    // Use GSAP ticker for animation loop
    gsap.ticker.add((time) => {
      this.lenis.raf(time * 1000);
    });

    // Disable GSAP's default lag smoothing for smoother animations
    gsap.ticker.lagSmoothing(0);

    // Handle anchor links
    this.handleAnchorLinks();

    // Handle data-lenis-prevent elements
    this.handlePreventElements();
  }

  /**
   * Smooth scroll to anchor links
   */
  handleAnchorLinks() {
    document.querySelectorAll('a[href^="#"]').forEach((anchor) => {
      anchor.addEventListener('click', (e) => {
        const href = anchor.getAttribute('href');

        // Skip if just "#"
        if (href === '#') return;

        const target = document.querySelector(href);
        if (target) {
          e.preventDefault();

          // Get header height for offset
          const header = document.querySelector('[data-header]');
          const offset = header ? -header.offsetHeight - 20 : -100;

          this.scrollTo(target, {
            offset,
            duration: 1.5,
          });
        }
      });
    });
  }

  /**
   * Handle elements that should prevent smooth scroll
   */
  handlePreventElements() {
    // Add data-lenis-prevent to elements that need native scroll
    const preventSelectors = [
      '.mobile-menu',
      '[data-lenis-prevent]',
    ];

    preventSelectors.forEach((selector) => {
      document.querySelectorAll(selector).forEach((el) => {
        if (!el.hasAttribute('data-lenis-prevent')) {
          el.setAttribute('data-lenis-prevent', '');
        }
      });
    });
  }

  /**
   * Scroll to target
   */
  scrollTo(target, options = {}) {
    this.lenis?.scrollTo(target, {
      duration: 1.5,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      ...options,
    });
  }

  /**
   * Stop scrolling
   */
  stop() {
    this.lenis?.stop();
  }

  /**
   * Start scrolling
   */
  start() {
    this.lenis?.start();
  }

  /**
   * Get scroll progress (0-1)
   */
  get progress() {
    if (!this.lenis) return 0;
    return this.lenis.scroll / (document.documentElement.scrollHeight - window.innerHeight);
  }

  /**
   * Get current scroll position
   */
  get scroll() {
    return this.lenis?.scroll || window.scrollY;
  }

  /**
   * Destroy Lenis instance
   */
  destroy() {
    this.lenis?.destroy();
    this.lenis = null;
  }
}
